import { cn } from '@/ui/lib/utils'

export type QuantScrubSpeed = 1 | 5 | 20 | 'max'
export type QuantScrubWindow = 120 | 240 | 480 | 'all'

export interface QuantChartScrubberProps {
  /** 0-based scrub bar. */
  k: number
  /** Total bars. 0 disables the whole control. */
  total: number
  playing: boolean
  speed: QuantScrubSpeed
  /** The pressed window button, or `null` after a manual zoom (no button is pressed). */
  window: QuantScrubWindow | null
  onScrub: (k: number) => void
  onTogglePlay: () => void
  onSpeed: (speed: QuantScrubSpeed) => void
  onWindow: (window: QuantScrubWindow) => void
  className?: string
}

const SPEEDS: QuantScrubSpeed[] = [1, 5, 20, 'max']
const WINDOWS: Array<{ value: QuantScrubWindow; label: string }> = [
  { value: 120, label: '120' },
  { value: 240, label: '240' },
  { value: 480, label: '480' },
  { value: 'all', label: 'All' },
]

function Segment({ active, label, onClick, disabled, group }: {
  active: boolean
  label: string
  onClick: () => void
  disabled: boolean
  group: string
}) {
  return (
    <button
      type="button"
      aria-label={`${group} ${label}`}
      aria-pressed={active}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'h-[26px] min-w-[34px] border-0 px-2 font-mono text-[10.5px] tracking-[0.06em] disabled:cursor-not-allowed',
        active ? 'bg-accent-line text-ink' : 'bg-transparent text-paper-50 enabled:cursor-pointer enabled:hover:text-paper',
      )}
    >
      {label}
    </button>
  )
}

/** 44px run-chart scrub bar (spec 036, handoff 3.4): play, range, `k / N`, speed, window. */
export function QuantChartScrubber({
  k,
  total,
  playing,
  speed,
  window,
  onScrub,
  onTogglePlay,
  onSpeed,
  onWindow,
  className,
}: QuantChartScrubberProps) {
  const off = total === 0
  return (
    <div
      data-scrubber
      className={cn('flex h-11 shrink-0 items-center gap-3 border-t border-paper-14 px-7 font-mono', className)}
    >
      <button
        type="button"
        aria-label={playing ? 'Pause playback' : 'Play'}
        disabled={off}
        onClick={onTogglePlay}
        className="h-[26px] w-[30px] shrink-0 cursor-pointer border-0 bg-accent-line text-[11px] text-ink disabled:cursor-not-allowed disabled:opacity-40"
      >
        {playing ? '❚❚' : '▶'}
      </button>
      <input
        type="range"
        aria-label="Scrub bar"
        min={0}
        max={Math.max(total - 1, 0)}
        value={Math.min(k, Math.max(total - 1, 0))}
        disabled={off}
        onChange={(e) => onScrub(Number(e.target.value))}
        className="min-w-20 flex-1 accent-accent-line disabled:cursor-not-allowed"
      />
      <span data-scrub-position className="min-w-[92px] shrink-0 text-right text-[11px] text-paper-90">
        {off ? '0 / 0' : `${k + 1} / ${total}`}
      </span>
      <div role="group" aria-label="Playback speed" className="flex shrink-0 border border-paper-14">
        {SPEEDS.map((s) => (
          <Segment
            key={s}
            group="Speed"
            label={s === 'max' ? 'max' : `${s}x`}
            active={speed === s}
            disabled={off}
            onClick={() => onSpeed(s)}
          />
        ))}
      </div>
      <div role="group" aria-label="Visible bars" className="flex shrink-0 border border-paper-14">
        {WINDOWS.map((w) => (
          <Segment key={w.label} group="Window" label={w.label} active={window === w.value} disabled={off} onClick={() => onWindow(w.value)} />
        ))}
      </div>
    </div>
  )
}
